/**
 * SURVEY RUNTIME — the respondent-side half of the survey contract.
 *
 * ⚠ THIS IS A PORT, NOT A COPY, and the difference matters.
 *
 * The Reception API and the Reception UI share `surveySchema.ts` / `surveyAnswers.ts` as byte-identical
 * mirrors, gated by a test that compares them character by character. www is plain JavaScript with a
 * different module system, so byte-identity is impossible here — which would normally mean this file
 * quietly drifts, and this codebase has been bitten by exactly that three separate times
 * (`duplicatePolicy`, the four decision-engine ports, the Outlook CTA copies).
 *
 * So the gate is BEHAVIOURAL instead of textual: `api-reception-v2/src/utils/surveyRuntimeParity.test.ts`
 * loads this file and runs the same corpus of questions and answers through both implementations,
 * asserting an identical verdict for every case. If a rule is changed on one side and not the other, that
 * test fails by name.
 *
 * WHY VALIDATE HERE AT ALL, given the server re-checks everything: so a respondent is corrected while she
 * is still looking at the field. The server is the boundary; this is the courtesy. They must agree, or
 * she is told "looks fine" and then refused with nothing on screen to explain it.
 */

/** Blocks carry no answer — they are layout or prose. Mirrors `NON_ANSWER_TYPES`. */
const NON_ANSWER_TYPES = ["html_note", "section_break"];

function isNonAnswer(type) {
  return NON_ANSWER_TYPES.indexOf(type) !== -1;
}

/**
 * Deliberately permissive — the same pattern the server uses, and for the same reason: a stricter regex
 * rejects real addresses (`+` tags, new TLDs, apostrophes), and a respondent whose genuine address is
 * refused simply leaves. One bounced email costs less than one lost response.
 */
const EMAIL = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

function isBlank(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function cfgNum(config, key) {
  const v = config ? config[key] : undefined;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function cfgStr(config, key) {
  const v = config ? config[key] : undefined;
  return typeof v === "string" ? v : "";
}

/** Option values a choice question accepts. Objects and plain strings both occur. */
export function allowedOptionValues(question) {
  const raw = question.config ? question.config.options : undefined;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(function (o) {
      if (typeof o === "string") return o;
      const obj = o || {};
      return String(obj.value !== undefined && obj.value !== null ? obj.value : obj.label || "");
    })
    .filter(function (v) {
      return v !== "";
    });
}

/**
 * Validate one answer. Returns a message a STRANGER can act on, or null.
 *
 * Every string here is duplicated verbatim in the server's copy. The parity test compares them, because
 * a respondent seeing one wording from the form and a different one from the server would reasonably
 * conclude something is broken.
 */
export function validateAnswer(question, value) {
  if (isNonAnswer(question.type) || question.type === "hidden") return null;

  const config = question.config || {};

  if (question.type === "consent") {
    if (question.required && value !== true) return "Please tick this to continue.";
    return null;
  }

  if (isBlank(value)) {
    return question.required ? "This one is required." : null;
  }

  switch (question.type) {
    case "email":
      return EMAIL.test(String(value).trim())
        ? null
        : "This doesn't look like an email address — check for a missing @ or a typo after the dot.";

    case "number": {
      const n = Number(String(value).trim());
      if (!isFinite(n)) return "Please enter a number.";
      const min = cfgNum(config, "min");
      const max = cfgNum(config, "max");
      if (min !== null && n < min) return "Please enter " + min + " or more.";
      if (max !== null && n > max) return "Please enter " + max + " or less.";
      return null;
    }

    case "phone": {
      const digits = String(value).replace(/[^\d]/g, "");
      if (digits.length < 6) return "Please enter a full phone number, including the country code.";
      if (digits.length > 20) return "That phone number is too long.";
      return null;
    }

    case "date": {
      const text = String(value).trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "Please pick a date.";
      const min = cfgStr(config, "min");
      const max = cfgStr(config, "max");
      // String comparison, so a timezone cannot shift the boundary by a day.
      if (min && text < min) return "Please pick a date on or after " + min + ".";
      if (max && text > max) return "Please pick a date on or before " + max + ".";
      return null;
    }

    case "select":
    case "radio": {
      const allowed = allowedOptionValues(question);
      if (allowed.length > 0 && allowed.indexOf(String(value)) === -1) return "Please choose one of the options.";
      return null;
    }

    case "checkbox": {
      const allowed = allowedOptionValues(question);
      const picked = Array.isArray(value) ? value.map(String) : [String(value)];
      for (let i = 0; i < picked.length; i += 1) {
        if (allowed.length > 0 && allowed.indexOf(picked[i]) === -1) return "Please choose from the options.";
      }
      const min = cfgNum(config, "minPick");
      const max = cfgNum(config, "maxPick");
      if (min !== null && picked.length < min) return "Please choose at least " + min + ".";
      if (max !== null && picked.length > max) return "Please choose no more than " + max + ".";
      return null;
    }

    case "rating":
    case "scale": {
      const n = Number(value);
      if (!isFinite(n)) return "Please choose a value.";
      const fallbackMin = question.type === "rating" ? 1 : 0;
      const fallbackMax = question.type === "rating" ? 5 : 10;
      const min = cfgNum(config, "min") === null ? fallbackMin : cfgNum(config, "min");
      const max = cfgNum(config, "max") === null ? fallbackMax : cfgNum(config, "max");
      if (n < min || n > max) return "Please choose a value between " + min + " and " + max + ".";
      return null;
    }

    case "file": {
      const keys = Array.isArray(value) ? value : [value];
      const maxFiles = cfgNum(config, "maxFiles") === null ? 1 : cfgNum(config, "maxFiles");
      if (keys.length > maxFiles) {
        return "Please attach no more than " + maxFiles + " file" + (maxFiles === 1 ? "" : "s") + ".";
      }
      return null;
    }

    case "text":
    case "textarea":
    default: {
      const text = String(value);
      const max = cfgNum(config, "maxLength");
      if (max !== null && text.length > max) return "Please keep this under " + max + " characters.";
      const pattern = cfgStr(config, "pattern");
      if (pattern) {
        try {
          if (!new RegExp(pattern).test(text)) return "This doesn't look right — please check it.";
        } catch (_) {
          // A malformed stored pattern must fail OPEN: rejecting a valid answer leaves the respondent
          // with no way to succeed at all.
          return null;
        }
      }
      return null;
    }
  }
}

/**
 * Validate a submission. `visibleKeys` limits it to the page in view, so a stepped survey does not demand
 * answers to questions the respondent has not reached — without it, a two-page form refuses to advance
 * past page one, forever.
 */
export function validateAnswers(questions, answers, visibleKeys) {
  const errors = {};
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i];
    if (isNonAnswer(q.type)) continue;
    if (visibleKeys && visibleKeys.indexOf(q.key) === -1) continue;
    const error = validateAnswer(q, answers ? answers[q.key] : undefined);
    if (error) errors[q.key] = error;
  }
  return errors;
}

/** The answer keys on one page, for `validateAnswers`. */
export function coerceVisibleKeys(pageQuestions) {
  const keys = [];
  for (let i = 0; i < pageQuestions.length; i += 1) {
    if (!isNonAnswer(pageQuestions[i].type)) keys.push(pageQuestions[i].key);
  }
  return keys;
}

/**
 * Split a flat question list into pages at `section_break` boundaries.
 *
 * Mirrors `pagesOf` in `surveySchema.ts`. A single-page survey is ONE page containing everything, so the
 * caller needs no special case — and a stepped survey with no breaks is also one page rather than an
 * error, which is what makes "layout is a setting" true on this side too.
 */
export function pagesOf(questions, layout) {
  const all = Array.isArray(questions) ? questions : [];
  if (layout !== "steps") return all.length ? [all] : [[]];

  const pages = [[]];
  for (let i = 0; i < all.length; i += 1) {
    const q = all[i];
    if (q.type === "section_break" && pages[pages.length - 1].length > 0) pages.push([q]);
    else pages[pages.length - 1].push(q);
  }
  const nonEmpty = pages.filter(function (p) {
    return p.length > 0;
  });
  return nonEmpty.length ? nonEmpty : [[]];
}
